import { homedir } from 'node:os';
import { join } from 'node:path';
import { auditSubagentMcpBindings, formatFinding } from '../lib/subagent-mcp-audit.js';

/**
 * ENG-5897 — `agt audit subagents` command.
 *
 * Walks the conventional sub-agent definition roots visible to a
 * Claude Code session — the user's `~/.claude/agents/`, every
 * installed plugin under `~/.claude/plugins/<plugin>/agents/`, and
 * the current working directory's `.claude/agents/` (if any) — and
 * reports each sub-agent whose `tools:` allowlist would block every
 * `mcp__*` tool on dispatch. That's the silent-fallback failure mode
 * ENG-5897 / ENG-5898 are closing the door on: a parent agent
 * dispatches one of these sub-agents to do MCP-tool work, the child
 * reports "No such tool available." for every `mcp__*` call, and (in
 * the worst case) reaches for raw secrets out of `.mcp.json` to
 * complete the task off-policy.
 *
 * Exit codes:
 *   0 — no findings (or some findings; we still exit 0 because
 *       restrictive plugin sub-agents are sometimes intentional)
 *   2 — only used by `--strict`, when at least one finding exists.
 *       Useful in CI to gate a deploy on a clean audit.
 */

interface AuditOptions {
  strict?: boolean;
  json?: boolean;
}

function defaultRoots(): string[] {
  const home = homedir();
  return [
    join(home, '.claude', 'agents'),
    join(home, '.claude', 'plugins'),
    join(process.cwd(), '.claude', 'agents'),
  ];
}

export async function auditSubagentsCommand(options: AuditOptions = {}): Promise<void> {
  const roots = defaultRoots();
  const findings = auditSubagentMcpBindings(roots);

  if (options.json) {
    process.stdout.write(`${JSON.stringify({ findings, roots }, null, 2)}\n`);
  } else if (findings.length === 0) {
    process.stdout.write('No restrictive sub-agents found — every sub-agent under the scanned roots either inherits the parent toolset or explicitly includes mcp__* wildcards.\n');
  } else {
    process.stdout.write(`Found ${findings.length} sub-agent${findings.length === 1 ? '' : 's'} whose tools allowlist excludes mcp__*:\n\n`);
    for (const f of findings) {
      process.stdout.write(`  • ${f.name}  (${f.file})\n`);
      process.stdout.write(`    tools: ${f.tools}\n`);
    }
    process.stdout.write(
      '\nThese sub-agents will return "No such tool available." for every mcp__* call when dispatched from a managed agent. ' +
        "If the parent needs to do MCP-tool work, dispatch the Augmented plugin's `augmented-worker` sub-agent instead — " +
        "it omits `tools:` so it inherits the parent's full toolset including every MCP server. See ENG-5897.\n",
    );
    // Structured log line per finding for log-grep workflows.
    for (const f of findings) {
      process.stderr.write(`${formatFinding(f)}\n`);
    }
  }

  if (options.strict && findings.length > 0) {
    process.exit(2);
  }
}
